if (!customElements.get('scroll-color-story')) {
  customElements.define('scroll-color-story', class ScrollColorStory extends HTMLElement {
    constructor() {
      super();
      this.currentProgress = 0;
      this.targetProgress = 0;
      this.timelineScrollLength = 110;
      this.timelineHoldLength = 15;
      this.animationFrame = null;
      this.ticking = false;
      this.screenBackgrounds = [];
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.animate = this.animate.bind(this);
    }

    connectedCallback() {
      this.section = this.closest('[data-scroll-color-story-section]');
      this.sticky = this.querySelector('[data-story-sticky]');
      this.track = this.querySelector('[data-story-track]');
      this.screens = Array.from(this.querySelectorAll('[data-story-screen]'));

      if (!this.section || !this.track || this.screens.length < 1) {
        return;
      }

      this.readTimelineSettings();
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize, { passive: true });

      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', this.handleResize);
      }

      this.measure();
      this.readScreenBackgrounds();
      this.updateTarget();
      this.currentProgress = this.targetProgress;
      this.applyProgress();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);

      if (this.mobileQuery && this.mobileQuery.removeEventListener) {
        this.mobileQuery.removeEventListener('change', this.handleResize);
      }

      if (this.animationFrame) {
        window.cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }

      if (this.track) {
        this.track.style.transform = '';
      }

      if (this.section) {
        this.section.style.removeProperty('--scroll-color-story-current-bg');
        this.section.style.removeProperty('--scroll-color-story-transition-shadow-opacity');
        this.section.style.removeProperty('--scroll-color-story-transition-glow-opacity');
        this.section.style.removeProperty('--scroll-color-story-transition-glow-scale');
        this.section.style.removeProperty('--scroll-color-story-transition-glow-color');
      }
    }

    handleResize() {
      this.readTimelineSettings();
      this.measure();
      this.readScreenBackgrounds();
      this.updateTarget();
      this.currentProgress = this.targetProgress;
      this.applyProgress();
    }

    handleScroll() {
      this.updateTarget();
      this.requestUpdate();
    }

    measure() {
      if (!this.section || !this.track) {
        return;
      }

      const sectionRect = this.section.getBoundingClientRect();
      this.sectionTop = sectionRect.top + window.scrollY;
      this.scrollDistance = Math.max(1, this.section.offsetHeight - window.innerHeight);
      this.maxTranslate = Math.max(0, this.track.scrollWidth - window.innerWidth);
    }

    updateTarget() {
      if (this.mobileQuery.matches) {
        this.targetProgress = 0;
        return;
      }

      const rawProgress = (window.scrollY - this.sectionTop) / this.scrollDistance;
      this.targetProgress = Math.min(Math.max(rawProgress, 0), 1);
    }

    requestUpdate() {
      if (this.ticking) {
        return;
      }

      this.ticking = true;
      this.animationFrame = window.requestAnimationFrame(this.animate);
    }

    animate() {
      if (this.reducedMotionQuery.matches) {
        this.currentProgress = this.targetProgress;
      } else {
        const damping = 0.085;
        this.currentProgress += (this.targetProgress - this.currentProgress) * damping;
      }

      if (Math.abs(this.targetProgress - this.currentProgress) < 0.0005) {
        this.currentProgress = this.targetProgress;
      }

      this.applyProgress();

      if (this.currentProgress !== this.targetProgress) {
        this.animationFrame = window.requestAnimationFrame(this.animate);
        return;
      }

      this.ticking = false;
      this.animationFrame = null;
    }

    applyProgress() {
      if (!this.track || !this.screens.length) {
        return;
      }

      if (this.mobileQuery.matches) {
        this.track.style.transform = '';
        this.applyBackground(0);
        this.setActiveScreen(0);
        return;
      }

      const motionState = this.getMotionState(this.currentProgress);
      const translateX = -this.maxTranslate * motionState.trackProgress;
      this.track.style.transform = `translate3d(${translateX}px, 0, 0)`;

      this.applyBackground(motionState.trackProgress);

      const activeIndex = Math.round(motionState.trackProgress * (this.screens.length - 1));
      this.setActiveScreen(activeIndex);
    }

    getMotionState(progress) {
      const screenCount = Math.max(this.screens.length, 1);
      const transitionCount = Math.max(screenCount - 1, 0);
      const scrollLength = Math.max(this.timelineScrollLength, 1);
      const holdLength = Math.max(this.timelineHoldLength, 0);
      const totalLength = scrollLength * transitionCount + holdLength * screenCount;
      const timelinePosition = this.clamp(progress, 0, 1) * totalLength;
      let cursor = 0;
      let trackProgress = 0;

      if (timelinePosition <= holdLength || transitionCount === 0) {
        return {
          trackProgress
        };
      }

      cursor = holdLength;

      for (let screenIndex = 0; screenIndex < transitionCount; screenIndex += 1) {
        const transitionStart = cursor;
        const transitionEnd = transitionStart + scrollLength;
        const startProgress = screenIndex / transitionCount;
        const endProgress = (screenIndex + 1) / transitionCount;

        if (timelinePosition <= transitionEnd) {
          const localProgress = this.smoothProgress((timelinePosition - transitionStart) / scrollLength);
          trackProgress = startProgress + (endProgress - startProgress) * localProgress;

          return {
            trackProgress
          };
        }

        cursor = transitionEnd;
        trackProgress = endProgress;

        if (timelinePosition <= cursor + holdLength) {
          return {
            trackProgress
          };
        }

        cursor += holdLength;
      }

      trackProgress = 1;

      return {
        trackProgress
      };
    }

    readTimelineSettings() {
      const scrollLength = Number.parseFloat(this.section?.dataset.scrollLength);
      const holdLength = Number.parseFloat(this.section?.dataset.holdLength);

      this.timelineScrollLength = Number.isFinite(scrollLength) ? scrollLength : 110;
      this.timelineHoldLength = Number.isFinite(holdLength) ? holdLength : 15;
    }

    readScreenBackgrounds() {
      this.screenBackgrounds = this.screens.map((screen) => {
        const screenStyles = window.getComputedStyle(screen);
        const screenColor = screenStyles.getPropertyValue('--screen-bg') || screenStyles.backgroundColor;

        return this.parseColor(screenColor) || { red: 0, green: 0, blue: 0 };
      });
    }

    applyBackground(trackProgress) {
      if (!this.screenBackgrounds.length) {
        return;
      }

      const transitionState = this.getBackgroundTransitionState(trackProgress);

      this.section.style.setProperty('--scroll-color-story-current-bg', this.formatColor(transitionState.backgroundColor));

      this.section.style.setProperty('--scroll-color-story-transition-shadow-opacity', transitionState.shadowOpacity.toFixed(4));
      this.section.style.setProperty('--scroll-color-story-transition-glow-opacity', transitionState.glowOpacity.toFixed(4));
      this.section.style.setProperty('--scroll-color-story-transition-glow-scale', transitionState.glowScale.toFixed(4));
      this.section.style.setProperty('--scroll-color-story-transition-glow-color', this.formatAlphaColor(transitionState.glowColor, transitionState.glowColorAlpha));
    }

    getBackgroundTransitionState(trackProgress) {
      if (this.screenBackgrounds.length === 1) {
        return {
          backgroundColor: this.screenBackgrounds[0],
          shadowOpacity: 0,
          glowOpacity: 0,
          glowScale: 1,
          glowColor: { red: 255, green: 255, blue: 255 },
          glowColorAlpha: 0
        };
      }

      const screenPosition = trackProgress * (this.screenBackgrounds.length - 1);
      const startIndex = Math.min(Math.floor(screenPosition), this.screenBackgrounds.length - 1);
      const endIndex = Math.min(startIndex + 1, this.screenBackgrounds.length - 1);
      const rawProgress = this.clamp(screenPosition - startIndex, 0, 1);

      if (startIndex === endIndex || rawProgress <= 0.001) {
        return {
          backgroundColor: this.screenBackgrounds[startIndex],
          shadowOpacity: 0,
          glowOpacity: 0,
          glowScale: 1,
          glowColor: { red: 255, green: 255, blue: 255 },
          glowColorAlpha: 0
        };
      }

      if (rawProgress >= 0.999) {
        return {
          backgroundColor: this.screenBackgrounds[endIndex],
          shadowOpacity: 0,
          glowOpacity: 0,
          glowScale: 1,
          glowColor: { red: 255, green: 255, blue: 255 },
          glowColorAlpha: 0
        };
      }

      const startColor = this.screenBackgrounds[startIndex];
      const endColor = this.screenBackgrounds[endIndex];
      const startLuminance = this.getColorLuminance(startColor);
      const endLuminance = this.getColorLuminance(endColor);
      const isDarkening = endLuminance < startLuminance;
      const colorDistance = this.getColorDistance(startColor, endColor);
      const contrastStrength = this.clamp(colorDistance / 255, 0, 1);
      const backgroundProgress = this.smootherProgress(rawProgress);
      const pulseProgress = Math.sin(backgroundProgress * Math.PI);
      const shadowOpacity = contrastStrength * pulseProgress * (isDarkening ? 0.28 : 0.1);
      const glowOpacity = contrastStrength * Math.pow(pulseProgress, 0.82) * (isDarkening ? 0.22 : 0.16);
      const glowScale = isDarkening
        ? 0.78 + backgroundProgress * 0.38
        : 1.1 - backgroundProgress * 0.24;
      const glowColor = isDarkening
        ? this.mixColors(startColor, { red: 255, green: 255, blue: 255 }, 0.35)
        : this.mixColors(endColor, { red: 255, green: 255, blue: 255 }, 0.62);
      const glowColorAlpha = isDarkening ? 0.2 : 0.16;

      return {
        backgroundColor: this.mixColors(startColor, endColor, backgroundProgress),
        shadowOpacity,
        glowOpacity,
        glowScale,
        glowColor,
        glowColorAlpha
      };
    }

    parseColor(colorValue) {
      const normalizedColor = colorValue.trim();

      if (normalizedColor.startsWith('#')) {
        return this.parseHexColor(normalizedColor);
      }

      const rgbMatch = normalizedColor.match(/rgba?\(([^)]+)\)/i);

      if (!rgbMatch) {
        return null;
      }

      const channels = rgbMatch[1].split(',').map((channel) => Number.parseFloat(channel.trim()));

      if (channels.length < 3 || channels.some((channel) => Number.isNaN(channel))) {
        return null;
      }

      return {
        red: channels[0],
        green: channels[1],
        blue: channels[2]
      };
    }

    parseHexColor(colorValue) {
      let hexColor = colorValue.replace('#', '').trim();

      if (hexColor.length === 3) {
        hexColor = hexColor.split('').map((channel) => channel + channel).join('');
      }

      if (hexColor.length !== 6) {
        return null;
      }

      return {
        red: Number.parseInt(hexColor.slice(0, 2), 16),
        green: Number.parseInt(hexColor.slice(2, 4), 16),
        blue: Number.parseInt(hexColor.slice(4, 6), 16)
      };
    }

    mixColors(startColor, endColor, progress) {
      const easedProgress = this.clamp(progress, 0, 1);

      return {
        red: Math.round(startColor.red + (endColor.red - startColor.red) * easedProgress),
        green: Math.round(startColor.green + (endColor.green - startColor.green) * easedProgress),
        blue: Math.round(startColor.blue + (endColor.blue - startColor.blue) * easedProgress)
      };
    }

    formatColor(color) {
      return `rgb(${color.red}, ${color.green}, ${color.blue})`;
    }

    formatAlphaColor(color, alpha) {
      return `rgba(${color.red}, ${color.green}, ${color.blue}, ${alpha})`;
    }

    getColorLuminance(color) {
      return (0.2126 * color.red + 0.7152 * color.green + 0.0722 * color.blue) / 255;
    }

    getColorDistance(startColor, endColor) {
      const redDelta = endColor.red - startColor.red;
      const greenDelta = endColor.green - startColor.green;
      const blueDelta = endColor.blue - startColor.blue;

      return Math.sqrt(redDelta * redDelta + greenDelta * greenDelta + blueDelta * blueDelta);
    }

    smoothProgress(progress) {
      const clampedProgress = this.clamp(progress, 0, 1);

      return clampedProgress * clampedProgress * (3 - 2 * clampedProgress);
    }

    smootherProgress(progress) {
      const clampedProgress = this.clamp(progress, 0, 1);

      return clampedProgress * clampedProgress * clampedProgress * (clampedProgress * (clampedProgress * 6 - 15) + 10);
    }

    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    }

    setActiveScreen(activeIndex) {
      this.screens.forEach((screen, index) => {
        screen.classList.toggle('is-active', index === activeIndex);
      });
    }
  });
}
